import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { CSkeleton, GradientTextAction, InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ValidationErrorItem, ControlCSV, ParseResult } from './types';
import { generateCsvTemplateLink, getFrameworkControlIds, pathNames } from './util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import CSelect from 'src/components/form/Select';
import { controllStatusOptions, strategyOptions } from 'src/api/types';
import { ControlCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Dropzone, DzoneWrap, ErrorItem, ErrorLog } from 'src/common/dropzone';
import {
  ControlInput,
  MaturityLevel,
  useControlFrameworksListQuery,
  useUpsertControlsMutation,
} from 'src/graphql/_generated';
import {
  convertDateToISO,
  convertToNumber,
  convertToString,
  convertToMaturityLevel,
  delimiters,
  validateRow,
} from 'src/utils/csv';
import { isQueryLoading, useCache } from 'src/graphql';
import { rmpValidationSchema } from 'src/components/formComposed/RmpForm/util';

interface FromCsvProps {
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
}

export const FromCsv: React.FC<FromCsvProps> = ({ onClose, setIsScvDirty, state }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [controlsProcessed, setControlsProcessed] = useState(0);
  const [totalControls, setTotalControls] = useState(0);
  const [validControls, setValidControls] = useState<ControlInput[]>([]);
  const { refetchQuery } = useCache();

  const { data: frameworksList, loading: isFrameworksLoading, networkStatus } = useControlFrameworksListQuery();
  const isFramewoksListLoading = isQueryLoading({ loading: isFrameworksLoading, data: frameworksList, networkStatus });

  const { file, setFile, readyForUpload, setReadyForUpload } = state;

  const isButtonDisabled = !file || (controlsProcessed < totalControls && controlsProcessed > 0);
  const csvTemplateLink = generateCsvTemplateLink();

  const [upsertControls, { loading }] = useUpsertControlsMutation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Add RMP modal',
        tags: ['RMP'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setError('');
        setIsUploadSuccessful(false);
        setValidationErrors([]);
        parseCSVToJSON(selectedFile);
        setIsScvDirty(true);
      }
    },
    [frameworksList],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  });

  const parseCSVToJSON = (file: File) => {
    const tryParse = (delimiter: string) =>
      new Promise<ParseResult<ControlCSV>>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          delimiter,
          complete: resolve,
          error: reject,
        });
      });

    (async () => {
      let result: ParseResult<ControlCSV> | null = null;
      for (const delim of delimiters) {
        const parsed = await tryParse(delim);
        if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
          result = parsed;
          break;
        }
      }

      if (!result || !result.data || result.data.length === 0) {
        setValidationErrors([
          { rowIndex: 0, errors: [{ path: 'Data', message: 'No control data found in the file.' }] },
        ]);
        return;
      }

      if (result.data.length > 60) {
        setError('The number of controls exceeds the limit of 60.');
        return;
      }

      const mappedControls: ControlInput[] = result.data.map((el) => {
        const frameworkControlIds = getFrameworkControlIds(el, frameworksList);

        return {
          frameworkControlIds,
          ...(el[ControlCsvHeaders.ID] && { id: convertToString(el[ControlCsvHeaders.ID]) }),
          name: el[ControlCsvHeaders.Name] || '', // required
          ...(el[ControlCsvHeaders.Strategy] && { strategy: convertToString(el[ControlCsvHeaders.Strategy]) }),
          ...(el[ControlCsvHeaders.Status] && { status: convertToString(el[ControlCsvHeaders.Status]) }),
          ...(el[ControlCsvHeaders.Owner] && { owner: { text: el[ControlCsvHeaders.Owner] } }),

          ...(el[ControlCsvHeaders.AnnualCost] && {
            annualCost: convertToNumber(el[ControlCsvHeaders.AnnualCost]),
          }),
          ...(el[ControlCsvHeaders.Vendor] && { vendor: convertToString(el[ControlCsvHeaders.Vendor]) }),
          ...(el[ControlCsvHeaders.ExpectedLiveDate] && {
            expectedActiveDate: convertDateToISO(el[ControlCsvHeaders.ExpectedLiveDate]),
          }),
          ...(el[ControlCsvHeaders.ActualLiveDate] && {
            actualActiveDate: convertDateToISO(el[ControlCsvHeaders.ActualLiveDate]),
          }),
          ...(el[ControlCsvHeaders.ReviewDate] && {
            reviewDate: convertDateToISO(el[ControlCsvHeaders.ReviewDate]),
          }),
          ...(el[ControlCsvHeaders.Description] && {
            description: convertToString(el[ControlCsvHeaders.Description]),
          }),
          ...(el[ControlCsvHeaders.Source] && { linkUrl: convertToString(el[ControlCsvHeaders.Source]) }),
          ...(el[ControlCsvHeaders.UrlName] && { linkText: convertToString(el[ControlCsvHeaders.UrlName]) }),
          ...(el[ControlCsvHeaders.ExistingMaturity] && {
            existingMaturity: convertToMaturityLevel(el[ControlCsvHeaders.ExistingMaturity]) as MaturityLevel,
          }),
          ...(el[ControlCsvHeaders.TargetMaturity] && {
            targetMaturity: convertToMaturityLevel(el[ControlCsvHeaders.TargetMaturity]) as MaturityLevel,
          }),
        };
      });

      const promises = mappedControls.map((row, idx) =>
        validateRow({ row, rowIndex: idx + 1, schema: rmpValidationSchema, pathNames }),
      );
      const results = await Promise.all(promises);
      const errorLog = results.filter((el) => el?.errors?.length) as ValidationErrorItem[];

      if (errorLog.length) {
        setValidationErrors(errorLog);
        setIsUploadSuccessful(false);
        setReadyForUpload(false);
      } else {
        setValidControls(mappedControls);
        setValidationErrors([]);
        setError('');
        setReadyForUpload(true);
      }
    })();
  };

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add RMP modal',
      tags: ['RMP'],
    });

    setTotalControls(validControls.length);
    setControlsProcessed(0);
    setReadyForUpload(false);

    const processedControls = validControls.map((el) => {
      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: { text: el.owner?.text || '' },
      });

      return { ...el, owner: userAdd };
    });

    try {
      await upsertControls({ variables: { controlsData: processedControls, workspaceId } });
      setControlsProcessed(validControls.length);

      queryCache.invalidateQueries();
      refetchQuery({ include: ['controls'] });

      setIsUploadSuccessful(true);
      setIsScvDirty(false);
      setReadyForUpload(false);
    } catch (error) {
      console.error('Error uploading controls:', error);
      setReadyForUpload(true);
    }
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setTotalControls(0);
    setControlsProcessed(0);
    setReadyForUpload(false);
  };

  return (
    <DzoneWrap className="h-padding">
      <a href={csvTemplateLink} download="control_template.csv" style={{ textDecoration: 'none' }}>
        <GradientTextAction
          $underline
          onClick={() =>
            mpEvent(MPEvents.DownloadCSVTemplate, {
              modal: 'Add RMP modal',
              tags: ['RMP'],
            })
          }
          weight={500}
        >
          Download CSV Template
        </GradientTextAction>
      </a>
      <Spacer $px={20} />
      {isFramewoksListLoading ? (
        <CSkeleton height={167} />
      ) : (
        <>
          {!isUploadSuccessful && (
            <Dropzone {...getRootProps()}>
              <input {...getInputProps()} />
              <Poppins px={14}>
                {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
              </Poppins>
            </Dropzone>
          )}
        </>
      )}

      {!isUploadSuccessful && <Spacer $px={20} />}
      {file && (
        <>
          <Poppins px={14}>File selected: {file.name}</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {file && validationErrors.length === 0 && (
        <>
          <Poppins px={14}>Controls ready for upload: {validControls.length}</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {!isUploadSuccessful && controlsProcessed < totalControls && (
        <>
          <Poppins px={14}>{`Adding controls: ${controlsProcessed}/${totalControls}`}</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {isUploadSuccessful && (
        <>
          <Poppins px={14}>File uploaded successfully!</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {readyForUpload && !isUploadSuccessful && (
        <>
          <Poppins px={14}>Validation successful, ready for upload</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {validationErrors.length > 0 && (
        <>
          <ErrorLog>
            {validationErrors.map((error, index) => (
              <ErrorItem key={index}>
                <Poppins color="error" px={16} weight={600}>
                  Error in row {error.rowIndex + 1}:
                </Poppins>
                {error.errors.map((e, idx) => (
                  <Poppins px={14} color="error" css="display: block" key={idx}>{`${e.path}: ${e.message}`}</Poppins>
                ))}
              </ErrorItem>
            ))}
          </ErrorLog>
          <Spacer $px={15} />
        </>
      )}
      {!isUploadSuccessful ? (
        <Button
          primary
          onClick={readyForUpload ? () => save() : resetUpload}
          css="width: 100%;"
          disabled={isButtonDisabled || !!validationErrors.length || loading}
        >
          {readyForUpload || loading ? 'Upload' : 'Select File'}
        </Button>
      ) : (
        <>
          <Button
            primary
            onClick={() => {
              resetUpload();
              setFile(null);
            }}
            css="width: 100%;"
          >
            Upload Another File
          </Button>
          <Spacer $px={20} />
          <Button primary onClick={onClose} css="width: 100%;">
            Done
          </Button>
        </>
      )}
      <Spacer $px={30} />
      {!file && (
        <div>
          <Poppins px={14}>The valid values of Strategy and Status are listed in the 2 drop down lists below.</Poppins>
          <Poppins css="display: block" px={14}>
            Blank is also valid for each.
          </Poppins>
          <Spacer $px={24} />
          <div className="grid-list">
            <div>
              <InputLabel>Strategy</InputLabel>
              <CSelect
                value={null}
                placeholder="Strategy"
                css="width: 100%;"
                menuPlacement="top"
                options={strategyOptions}
              />
            </div>
            <div>
              <InputLabel>Status</InputLabel>
              <CSelect
                value={null}
                placeholder="Status"
                css="width: 100%;"
                menuPlacement="top"
                options={controllStatusOptions}
              />
            </div>
          </div>
          <Spacer $px={20} />
        </div>
      )}
      {error && (
        <>
          <Spacer $px={20} />
          <div className="error">{error}</div>
        </>
      )}
    </DzoneWrap>
  );
};
